import React from 'react';

import { NAV_DESKTOP } from '../../theme/tokens';

/** What Tab can land on inside a dialog: a native control or any element in the tab order, none taken out of it. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
  '[contenteditable="true"]',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(', ');

/** A modal dialog: the studio's Sheet and Modal, a confirmation laid over one. */
const MODAL_DIALOG = '[role="dialog"][aria-modal="true"]';

/** The studio's content card: from NAV_DESKTOP up, a dialog's dim covers it alone (contentAreaOverlay). */
const CONTENT_CARD = '.pg-body-content';

/** Where the dim covers only the content card (the chrome beside it stays live). */
const CHROME_LIVE_QUERY = NAV_DESKTOP.replace('@media ', '');

/** No control holds the focus: it fell to the body (as a dialog holding it unmounted). */
const focusIsLost = (): boolean => document.activeElement === null || document.activeElement === document.body;

/** A stop Tab reaches: laid out, and in no inert part (a closed accordion's body keeps its box, inert). */
const isTabStop = (el: Element): boolean => el.getClientRects().length > 0 && el.closest('[inert]') === null;

/** `b` comes after `a` in the page. */
const follows = (a: Node, b: Node): boolean => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

/** Another modal dialog is laid over `dialog`: shown, after it in the page (a confirmation it opened). */
export const dialogOver = (dialog: HTMLElement): boolean =>
  Array.from(document.querySelectorAll(MODAL_DIALOG)).some(
    (other) => other !== dialog && !dialog.contains(other) && follows(dialog, other) && isTabStop(other),
  );

/**
 * The focus is somewhere the dialog does not cover, so Tab is its own there: a layer laid after the dialog (the list a
 * menu in it opened), or, from NAV_DESKTOP up, the studio's chrome the dim leaves live (the top bar, the side menu,
 * Lumio's column).
 */
const focusStandsApart = (active: Element | null, dialog: HTMLElement): boolean => {
  if (!(active instanceof HTMLElement) || active === document.body || active === dialog) return false;
  if (follows(dialog, active)) return true;
  return window.matchMedia(CHROME_LIVE_QUERY).matches && active.closest(CONTENT_CARD) === null;
};

type Opener = { el: HTMLElement | null; dialog: HTMLElement | null };

/**
 * A dialog's focus, as ows-ftux's Modal and Sheet hold it: the element that had the focus when the dialog opened is
 * remembered in the opening render (before any child's autoFocus runs), the focus moves into the dialog unless a child
 * already took it, and it goes back to that opener as the dialog closes; gone with what it was in (a row the dialog
 * removed) or refusing it (a button the dialog's own action disabled, as a confirmed top-up's), to the dialog it was
 * in. Only from the body, where it falls with the dialog: a focus the owner put somewhere else meanwhile stays there.
 * `active`: the dialog shows (a dialog its parent keeps mounted).
 */
export const useDialogFocus = (dialogRef: React.RefObject<HTMLElement>, active = true): void => {
  const opener = React.useRef<Opener | null>(null);
  if (!active) {
    opener.current = null;
  } else if (opener.current === null) {
    const el = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    opener.current = { el, dialog: el?.closest<HTMLElement>(MODAL_DIALOG) ?? null };
  }
  React.useEffect(() => {
    if (!active) return undefined;
    const back = opener.current;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    return () => {
      if (!back || !focusIsLost()) return;
      // The first of them that takes the focus: focus() on a disabled control does nothing.
      [back.el, back.dialog].some((el) => {
        if (!el || !el.isConnected || !isTabStop(el)) return false;
        el.focus({ preventScroll: true });
        return document.activeElement === el;
      });
    };
  }, [active, dialogRef]);
};

/**
 * Tab and Shift+Tab go round the dialog's own controls, as aria-modal promises the page behind is inert: Tab from its
 * last control (or from the dialog itself, or from the page it covers) goes to its first, Shift+Tab from its first to
 * its last. With no control, the dialog itself keeps the focus. It stands down while another modal dialog lies over it
 * or the focus is somewhere it does not cover (focusStandsApart).
 */
export const keepTabIn = (event: KeyboardEvent, dialog: HTMLElement | null): void => {
  if (!dialog || dialogOver(dialog)) return;
  const active = document.activeElement;
  const inside = active instanceof HTMLElement && active !== dialog && dialog.contains(active);
  if (!inside && focusStandsApart(active, dialog)) return;
  const controls = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isTabStop);
  if (controls.length === 0) {
    event.preventDefault();
    dialog.focus({ preventScroll: true });
    return;
  }
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && (!inside || active === first)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (!inside || active === last)) {
    event.preventDefault();
    first.focus();
  }
};
